// frontend/src/pages/VideoPhotoEditor.js
//
// RETARGET (2026-08-13). The page was titled "Social Media Video & Photo Editor"
// — a phrase nobody searches — and sat at position 29.12 on 212 impressions.
// 125 editor terms worth 521,500 searches sit at competition <= 40:
//   online video editor           10K-100K  $0.51  comp 21
//   video editing app for tiktok   1K-10K   $1.46  comp 9
//   video editing app for instagram 1K-10K  $1.21  comp 11
//   online video editing tool      1K-10K   $1.01  comp 13
//   add text to video              1K-10K   $0.61  comp 28
//   online video cutter            1K-10K   $0.52  comp 1
//
// FREE TERMS ARE TARGETED, at the owner's direction (2026-08-13). 47 "free"
// editor terms carry 298,000 searches:
//   free software for video editing  10K-100K  $1.41  comp 39
//   free app for video editing       10K-100K  $0.85  comp 13
//   free online video editor no watermark  1K-10K  $0.51  comp 33
//
// ⚠️ PRODUCT CHANGE REQUIRED: the editor is currently credit-gated (see the
// video-credit tracking in VideoEditorModal.js). The page now says free, so the
// gate has to come off — or a genuinely free tier has to exist — before this is
// true. Tracked in docs/SEO_PROGRESS.md.
//
// No watermark is applied on render, which is a real differentiator: most free
// online editors watermark, and `no watermark` is its own keyword group.
//
// The previous copy also claimed saved
// colour palettes, font stacks, frame-level commenting, Canva import and 4K
// original storage; none of those exist in the editor components, and all are
// removed here.
//
// Real capabilities, from pages/dashboard/post/components/videoEditor/:
//   TrimTimeline · CropPreview · TextPanel · FiltersPanel · AudioPanel ·
//   MusicLibraryModal
//
// Screenshot is the real product capture — do not replace with stock.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Image, Button, Card, Accordion } from 'react-bootstrap'
import { Scissors, Crop, Fonts, Sliders, MusicNoteBeamed, CheckCircleFill } from 'react-bootstrap-icons'

const editorShot = '/assets/logo/videoeditor.webp'

const TOOLS = [
  { icon: <Scissors />,        title: 'Trim and cut',   body: 'Drag the timeline to cut a clip to length. Useful when a 90-second recording needs to be a 30-second Reel.' },
  { icon: <Crop />,            title: 'Crop and resize', body: 'Reframe to 9:16 for Reels, Shorts and TikTok, 1:1 for a feed post, or 4:5 for the most Instagram feed space.' },
  { icon: <Fonts />,           title: 'Add text',       body: 'Overlay captions, titles and calls to action directly on the video.' },
  { icon: <Sliders />,         title: 'Filters',        body: 'Adjust the look without exporting to another app and re-importing.' },
  { icon: <MusicNoteBeamed />, title: 'Add music',      body: 'Pick a track from the built-in library, or bring your own audio.' },
  { icon: <CheckCircleFill />, title: 'Straight to the queue', body: 'The finished video schedules from the same screen. No export, no re-upload.' },
]

const SIZES = [
  ['Instagram Reels', '1080 × 1920', '9:16'],
  ['Instagram feed',  '1080 × 1350', '4:5'],
  ['TikTok',          '1080 × 1920', '9:16'],
  ['YouTube Shorts',  '1080 × 1920', '9:16'],
  ['Facebook feed',   '1080 × 1350', '4:5'],
  ['LinkedIn',        '1080 × 1080', '1:1'],
]

const FAQS = [
  ['Do I need to install anything?',
   'No. The editor runs in your browser — there is nothing to download and it works on any machine.'],
  ['Can I edit video for TikTok and Instagram Reels?',
   'Yes. Crop to 9:16, trim to length, add text, then schedule to TikTok, Reels and YouTube Shorts from the same screen.'],
  ['Can I add text to a video?',
   'Yes. Overlay titles, captions or a call to action, and position them on the frame.'],
  ['Can I trim or cut a video?',
   'Yes. Drag the timeline handles to set where the clip starts and ends.'],
  ['Can I add music?',
   'Yes. Choose a track from the built-in library, or upload your own audio.'],
  ['Is the video editor free?',
   'Yes. Trim, crop, add text, apply filters and add music at no cost, and your video is exported without a watermark. A paid plan adds scheduling to eight networks, bulk uploads and the content calendar, from $9/month.'],
  ['Does it add a watermark?',
   'No. Most free online editors stamp their logo on your export — this one does not.'],
  ['Do I have to export and re-upload?',
   'No — that is the point of editing in the same place you schedule. The finished video goes straight into your queue.'],
]

export default function VideoPhotoEditor () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <h1 className='display-5 fw-bold mb-3'>Free Online Video Editor for Social Media</h1>
              <p className='lead mb-4'>
                Trim, crop, add text and drop in music — free, in your browser, with no
                watermark. Then schedule straight to Instagram, TikTok, YouTube and five
                more networks.
              </p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Start free trial
              </Button>
              <Button variant='outline-primary' size='lg' href='/plans' className='mb-2'>
                See plans
              </Button>
              <p className='text-muted small mt-3 mb-0'>
                Free to use · no watermark · nothing to install
              </p>
            </Col>
            <Col md={6}>
              <Image src={editorShot} alt='The Greatly Social video editor, showing the trim timeline and editing tools'
                     fluid className='shadow-card rounded-3 border' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── DEFINITION (snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            An <strong>online video editor</strong> lets you cut, crop and caption a video in
            your browser rather than in desktop software. For social media that matters more
            than it sounds: every network wants a different shape, and re-exporting the same
            clip four times in Premiere or iMovie is where an afternoon goes.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Editing where you schedule removes the export-and-re-upload loop entirely.
          </p>
        </Container>
      </section>

      {/* ───────── TOOLS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-0'>What you can do</h2>
            </Col>
          </Row>
          <Row className='g-4'>
            {TOOLS.map(({ icon, title, body }) => (
              <Col md={4} key={title}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='fs-3 text-primary mb-2'>{icon}</div>
                    <Card.Title className='fw-semibold h6'>{title}</Card.Title>
                    <Card.Text className='text-muted small'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PER-PLATFORM ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 820 }}>
          <h2 className='fw-bold mb-2'>Editing video for each network</h2>
          <p className='text-muted fs-5 mb-4'>
            The most common reason a scheduled video fails is the wrong shape. Crop once for
            each destination and it stops being a problem.
          </p>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr><th>Network</th><th>Size</th><th>Ratio</th></tr>
              </thead>
              <tbody>
                {SIZES.map(([n, s, r]) => (
                  <tr key={n}>
                    <td className='fw-semibold'>{n}</td>
                    <td className='text-muted'>{s}</td>
                    <td className='text-muted'>{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            One vertical video covers Reels, TikTok and Shorts — crop it once and
            <a href='/bulk-posting'> schedule it to all three in the same post</a>.
          </p>
        </Container>
      </section>

      {/* ───────── WHY IN-APP ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-3'>Why edit where you schedule</h2>
          <p className='text-muted fs-5'>
            The usual workflow is: edit in one app, export, upload to another, discover the
            crop is wrong, go back. Doing both in one place removes three of those steps.
          </p>
          <ul className='text-muted fs-5 mb-0'>
            <li className='mb-2'>No export and re-upload between editing and scheduling</li>
            <li className='mb-2'>Crop to each platform&rsquo;s ratio before the post is queued, not after it fails</li>
            <li className='mb-2'>Nothing to install, on any machine you happen to be using</li>
            <li>The finished video lands on your <a href='/calendar-view'>content calendar</a> with everything else</li>
          </ul>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Video editor FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can edit free, then schedule everywhere</h2>
          <p className='lead mb-4'>
            The editor is free and watermark-free. Add scheduling to eight networks from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
